/**
 * @fileoverview React Query hooks for room management operations (Admin role)
 * @author Hospital Room Scheduler Team
 * @created 08-10-2025 02:01:26
 * 
 * Comprehensive hooks for room CRUD operations, detail views, and statistics.
 * Follows established patterns from useBuildings.ts with proper caching,
 * invalidation strategies, and optimistic updates.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../useAuth';
import {
  getRooms,
  getRoomById,
  createRoom,
  updateRoom,
  deleteRoom,
  getRoomStats,
  getRoomDetail,
  getRoomSchedule,
  getRoomSessionTimes,
  getRoomBlocks,
  getRoomConflicts,
  getRoomsForSelection,
  type GetRoomsParams,
  type UpdateRoomData,
  type RoomWithDetails,
  type GetRoomStatsParams,
} from '../../api/endpoints/rooms.js';

// =============================================================================
// QUERY KEYS
// =============================================================================

/**
 * Centralized query keys for room-related queries
 * Hierarchical structure enables efficient cache invalidation
 */
export const roomQueryKeys = {
  all: ['rooms'] as const,
  lists: () => [...roomQueryKeys.all, 'list'] as const,
  list: (params: GetRoomsParams) => [...roomQueryKeys.lists(), params] as const,
  details: () => [...roomQueryKeys.all, 'detail'] as const,
  detail: (id: string) => [...roomQueryKeys.details(), id] as const,
  detailData: (id: string, dateRange?: { startDate: string; endDate: string }) => 
    [...roomQueryKeys.detail(id), 'data', dateRange] as const,
  schedule: (id: string, startDate: string, endDate: string) => 
    [...roomQueryKeys.detail(id), 'schedule', { startDate, endDate }] as const,
  sessionTimes: (id: string) => 
    [...roomQueryKeys.detail(id), 'session-times'] as const,
  blocks: (id: string, startDate: string, endDate: string) => 
    [...roomQueryKeys.detail(id), 'blocks', { startDate, endDate }] as const,
  conflicts: (id: string, startDate: string, endDate: string) => 
    [...roomQueryKeys.detail(id), 'conflicts', { startDate, endDate }] as const,
  stats: (params: GetRoomStatsParams) => 
    [...roomQueryKeys.all, 'stats', params] as const,
  selection: () => [...roomQueryKeys.all, 'selection'] as const,
};

// =============================================================================
// ROOM LIST HOOKS
// =============================================================================

/**
 * Hook to get rooms list with pagination, filtering, and sorting
 * @param params - Query parameters for filtering and pagination
 * @returns Room list query result with React Query state management
 * 
 * @example
 * const { data, isLoading, error } = useRooms({
 *   page: 1,
 *   limit: 20,
 *   search: 'Room A',
 *   buildingId: 'building-uuid',
 *   isActive: true
 * });
 */
export function useRooms(params: GetRoomsParams = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.list(params),
    queryFn: () => getRooms(params),
    enabled: !!token,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes (formerly cacheTime)
    retry: (failureCount, error: any) => {
      // Don't retry on authentication or authorization errors
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get rooms for selection/dropdown components
 * Optimized for UI components that need full list of rooms
 * @returns Rooms selection query result
 * 
 * @example
 * const { data: rooms } = useRoomsForSelection();
 * // Use in Select component: data={rooms?.map(r => ({ value: r.id, label: r.name }))}
 */
export function useRoomsForSelection() {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.selection(),
    queryFn: () => getRoomsForSelection(),
    enabled: !!token,
    staleTime: 10 * 60 * 1000, // 10 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =============================================================================
// ROOM DETAIL HOOKS
// =============================================================================

/**
 * Hook to get single room by ID
 * @param id - Room ID (UUID)
 * @returns Room detail query result
 * 
 * @example
 * const { data: room, isLoading } = useRoom(roomId);
 */
export function useRoom(id: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.detail(id),
    queryFn: () => getRoomById(id),
    enabled: !!token && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get comprehensive room detail data
 * Includes room info, schedule, blocks, conflicts, and utilization
 * @param id - Room ID (UUID)
 * @param dateRange - Optional date range for filtering
 * @returns Room detail data query result
 * 
 * @example
 * const { data } = useRoomDetail(roomId, {
 *   startDate: '2025-01-01',
 *   endDate: '2025-01-31'
 * });
 */
export function useRoomDetail(
  id: string,
  dateRange?: { startDate: string; endDate: string }
) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.detailData(id, dateRange),
    queryFn: () => getRoomDetail(id, dateRange),
    enabled: !!token && !!id,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get room schedule data
 * @param id - Room ID (UUID)
 * @param startDate - Start date (ISO format)
 * @param endDate - End date (ISO format)
 * @returns Room schedule query result
 * 
 * @example
 * const { data: schedule } = useRoomSchedule(roomId, '2025-01-01', '2025-01-07');
 */
export function useRoomSchedule(id: string, startDate: string, endDate: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.schedule(id, startDate, endDate),
    queryFn: () => getRoomSchedule(id, startDate, endDate),
    enabled: !!token && !!id && !!startDate && !!endDate,
    staleTime: 1 * 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get room session times
 * @param id - Room ID (UUID)
 * @returns Room session times query result
 * 
 * @example
 * const { data: sessionTimes } = useRoomSessionTimes(roomId);
 */
export function useRoomSessionTimes(id: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.sessionTimes(id),
    queryFn: () => getRoomSessionTimes(id),
    enabled: !!token && !!id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get room blocks (maintenance/cleaning periods)
 * @param id - Room ID (UUID)
 * @param startDate - Start date (ISO format)
 * @param endDate - End date (ISO format)
 * @returns Room blocks query result
 * 
 * @example
 * const { data: blocks } = useRoomBlocks(roomId, '2025-01-01', '2025-01-31');
 */
export function useRoomBlocks(id: string, startDate: string, endDate: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.blocks(id, startDate, endDate),
    queryFn: () => getRoomBlocks(id, startDate, endDate),
    enabled: !!token && !!id && !!startDate && !!endDate,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get room conflicts
 * @param id - Room ID (UUID)
 * @param startDate - Start date (ISO format)
 * @param endDate - End date (ISO format)
 * @returns Room conflicts query result
 * 
 * @example
 * const { data: conflicts } = useRoomConflicts(roomId, '2025-01-01', '2025-01-31');
 */
export function useRoomConflicts(id: string, startDate: string, endDate: string) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.conflicts(id, startDate, endDate),
    queryFn: () => getRoomConflicts(id, startDate, endDate),
    enabled: !!token && !!id && !!startDate && !!endDate,
    staleTime: 1 * 60 * 1000, // 1 minute (conflicts need fresh data)
    gcTime: 3 * 60 * 1000, // 3 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403') || error?.message?.includes('404')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

/**
 * Hook to get room statistics
 * @param params - Filter parameters (buildingId, floorId)
 * @returns Room statistics query result
 * 
 * @example
 * const { data: stats } = useRoomStats({ buildingId: 'building-uuid' });
 */
export function useRoomStats(params: GetRoomStatsParams = {}) {
  const { getStoredToken } = useAuth();
  const token = getStoredToken();

  return useQuery({
    queryKey: roomQueryKeys.stats(params),
    queryFn: () => getRoomStats(params),
    enabled: !!token,
    staleTime: 2 * 60 * 1000, // 2 minutes
    gcTime: 5 * 60 * 1000, // 5 minutes
    retry: (failureCount, error: any) => {
      if (error?.message?.includes('401') || error?.message?.includes('403')) {
        return false;
      }
      return failureCount < 3;
    },
  });
}

// =============================================================================
// ROOM MUTATION HOOKS
// =============================================================================

/**
 * Hook to create a new room
 * Automatically invalidates room list queries on success
 * @returns Room creation mutation with loading/error states
 * 
 * @example
 * const createRoomMutation = useCreateRoom();
 * createRoomMutation.mutate({
 *   number: 'A101',
 *   name: 'Operating Room 1',
 *   floorId: 'floor-uuid',
 *   capacity: 5,
 *   allowedSpecialties: ['CARDIOLOGY', 'GENERAL_SURGERY']
 * });
 */
export function useCreateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createRoom,
    onSuccess: (data) => {
      // Invalidate all room list queries to refetch with new room
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.selection() });
      
      // Show success notification
      notifications.show({
        title: 'Success',
        message: `Room "${data.number} - ${data.name}" created successfully`,
        color: 'green',
      });
    },
    onError: (error: any) => {
      // Show error notification with specific message
      const errorMessage = error?.message || 'Failed to create room';
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    },
  });
}

/**
 * Hook to update an existing room
 * Automatically invalidates affected queries on success
 * @returns Room update mutation with loading/error states
 * 
 * @example
 * const updateRoomMutation = useUpdateRoom();
 * updateRoomMutation.mutate({
 *   id: 'room-uuid',
 *   roomData: { capacity: 10, isActive: true }
 * });
 */
export function useUpdateRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roomData }: { id: string; roomData: UpdateRoomData }) => 
      updateRoom(id, roomData),
    onMutate: async ({ id, roomData }) => {
      // Cancel outgoing queries for optimistic update
      await queryClient.cancelQueries({ queryKey: roomQueryKeys.detail(id) });

      // Snapshot previous value for rollback
      const previousRoom = queryClient.getQueryData<RoomWithDetails>(roomQueryKeys.detail(id));

      // Optimistically update the cache
      if (previousRoom) {
        queryClient.setQueryData<RoomWithDetails>(roomQueryKeys.detail(id), {
          ...previousRoom,
          ...roomData,
          updatedAt: new Date().toISOString(),
        });
      }

      return { previousRoom };
    },
    onSuccess: (data, variables) => {
      // Update the detail cache with actual server response
      queryClient.setQueryData(roomQueryKeys.detail(variables.id), data);
      
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.selection() });
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.stats({}) });
      
      // Show success notification
      notifications.show({
        title: 'Success',
        message: `Room "${data.number} - ${data.name}" updated successfully`,
        color: 'green',
      });
    },
    onError: (error: any, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousRoom) {
        queryClient.setQueryData(roomQueryKeys.detail(variables.id), context.previousRoom);
      }
      
      // Show error notification
      const errorMessage = error?.message || 'Failed to update room';
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    },
  });
}

/**
 * Hook to delete a room (soft delete)
 * Automatically invalidates affected queries on success
 * @returns Room deletion mutation with loading/error states
 * 
 * @example
 * const deleteRoomMutation = useDeleteRoom();
 * deleteRoomMutation.mutate('room-uuid');
 */
export function useDeleteRoom() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteRoom,
    onMutate: async (roomId: string) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: roomQueryKeys.detail(roomId) });

      // Snapshot for rollback
      const previousRoom = queryClient.getQueryData<RoomWithDetails>(roomQueryKeys.detail(roomId));

      // Optimistically update - mark as inactive
      if (previousRoom) {
        queryClient.setQueryData<RoomWithDetails>(roomQueryKeys.detail(roomId), {
          ...previousRoom,
          isActive: false,
        });
      }

      return { previousRoom };
    },
    onSuccess: (data, roomId) => {
      // Invalidate all related queries
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.lists() });
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.selection() });
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.detail(roomId) });
      queryClient.invalidateQueries({ queryKey: roomQueryKeys.stats({}) });
      
      // Show success notification
      notifications.show({
        title: 'Success',
        message: data.message || 'Room deactivated successfully',
        color: 'green',
      });
    },
    onError: (error: any, roomId, context) => {
      // Rollback optimistic update
      if (context?.previousRoom) {
        queryClient.setQueryData(roomQueryKeys.detail(roomId), context.previousRoom);
      }
      
      // Show error notification
      const errorMessage = error?.message || 'Failed to delete room';
      notifications.show({
        title: 'Error',
        message: errorMessage,
        color: 'red',
      });
    },
  });
}

